/**
 * Chapter1Engine — one canvas, one entity pool, one rAF loop. Every
 * frame runs the current beat's layout to set target positions, then
 * lerps entities toward their targets, then renders dots + decorations.
 *
 * The architecture that makes transitions fluid: entities are never
 * created or destroyed between beats. Entity 3 is the same ball in
 * every beat — just at different target positions with different
 * target colors. When the beat flips, the entity starts moving toward
 * its new home. The continuity IS the animation.
 */
import { useEffect, useRef } from "react";
import {
  type BeatLayoutFn,
  type DecoEntry,
  type Entity,
  type LabelEntry,
  ENTITY_COUNT,
} from "./Chapter1Layouts";
import { CREAM_RGB } from "./designTokens";
import type { AudioEngineHandle, HumSource } from "./AudioEngine";
import {
  DEFAULT_TILT,
  drawDot,
  drawLabel,
  drawLine,
  project,
  WARMTH_RGB,
  type CameraOpts,
  type Warmth,
} from "./scene3d";

// Position and visual-style props lerp at a deliberate rate — motion
// feels intentional, not snappy. Wave params lerp much slower so the
// interference pattern doesn't tear up during beat transitions. Freq
// is the most sensitive (a change of 0.5 at distance 1.5 is a 0.75
// radian jump in the sine argument), so it gets its own, even slower
// rate.
const POS_LERP = 0.038;
const WAVE_LERP = 0.02;
const FREQ_LERP = 0.01;

// Trail ring-buffer depth. Each entity keeps a short history of screen
// positions; when rendered with additive compositing, the history
// paints a luminous wake behind the entity so every node reads as a
// moving fireball of light instead of a 2D dot. TRAIL_STRIDE skips
// frames so the trail spans ~2x more distance for the same buffer
// cost — a longer wisp without doubling work.
const TRAIL_SAMPLES = 10;
const TRAIL_STRIDE = 2;

interface Props {
  layout: BeatLayoutFn;
  /** Time in seconds the current beat has been active (resets on beat change). */
  beatTime: number;
  cameraCenterRatio?: number; // where to put the scene vertically (0..1); default 0.42
  /** Optional audio engine handle — when provided, the render loop
   *  streams active wave-emitters to the engine each frame so every
   *  emitter gets a sustained hum whose pitch follows its wave rate. */
  audioEngine?: AudioEngineHandle | null;
}

interface TrailBuffer {
  xs: Float32Array;
  ys: Float32Array;
  alphas: Float32Array;
  radii: Float32Array;
  head: number;
  filled: boolean;
  frame: number;
}

function makeTrailBuffer(): TrailBuffer {
  return {
    xs: new Float32Array(TRAIL_SAMPLES),
    ys: new Float32Array(TRAIL_SAMPLES),
    alphas: new Float32Array(TRAIL_SAMPLES),
    radii: new Float32Array(TRAIL_SAMPLES),
    head: 0,
    filled: false,
    frame: 0,
  };
}

export function Chapter1Engine({ layout, beatTime, cameraCenterRatio = 0.42, audioEngine }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const entitiesRef = useRef<Entity[] | null>(null);
  const trailsRef = useRef<TrailBuffer[] | null>(null);
  const layoutRef = useRef(layout);
  const beatTimeRef = useRef(beatTime);
  const audioRef = useRef<AudioEngineHandle | null>(audioEngine ?? null);
  audioRef.current = audioEngine ?? null;

  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  useEffect(() => {
    beatTimeRef.current = beatTime;
  }, [beatTime]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (!entitiesRef.current) {
      entitiesRef.current = Array.from({ length: ENTITY_COUNT }, (_, i) => ({
        id: i,
        x: 0,
        y: 0,
        z: 0,
        tx: 0,
        ty: 0,
        tz: 0,
        size: 0.8,
        targetSize: 0.8,
        alpha: 0,
        targetAlpha: 0,
        warmth: "cream" as const,
        targetWarmth: "cream" as const,
        halo: 0,
        targetHalo: 0,
        pulse: false,
        emit: 0,
        targetEmit: 0,
        emitFreq: 5,
        targetEmitFreq: 5,
        emitSpeed: 3.5,
        targetEmitSpeed: 3.5,
        waveR: 242,
        waveG: 238,
        waveB: 228,
        targetWaveR: 242,
        targetWaveG: 238,
        targetWaveB: 228,
        phase: 0,
      }));
    }

    if (!trailsRef.current) {
      trailsRef.current = Array.from({ length: ENTITY_COUNT }, () =>
        makeTrailBuffer()
      );
    }

    const dpr = window.devicePixelRatio || 1;
    let w = 0;
    let h = 0;

    const resize = (): void => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const reduceMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    const start = performance.now();
    let lastTime = start;
    let raf = 0;

    // Smoothed mesh-alpha and camera-zoom. Layouts declare targets via
    // the BeatLayoutResult; these lerp toward them so the field can
    // fade out (beat 18) or the camera can pull back (beat 20) without
    // snapping.
    let meshAlphaMul = 1;
    let cameraZoom = 1;
    let meshExtentMul = 1;
    let cameraRotYOffset = 0;
    let cameraTiltOffset = 0;
    const OPT_LERP = 0.035;

    const tick = (time: number): void => {
      const globalT = reduceMotion ? 0 : (time - start) / 1000;
      const dt = reduceMotion
        ? 0
        : Math.min(0.05, Math.max(0, (time - lastTime) / 1000));
      lastTime = time;
      const bt = reduceMotion ? 0 : beatTimeRef.current;
      const entities = entitiesRef.current!;

      // 1) Run current layout — sets target values + returns decorations/labels
      const result = layoutRef.current(entities, bt);

      // Lerp mesh alpha + zoom + mesh extent + rot offset toward
      // the beat's declared targets.
      const targetMeshAlphaMul = result.meshAlphaMul ?? 1;
      const targetCameraZoom = result.cameraZoom ?? 1;
      const targetMeshExtentMul = result.meshExtentMul ?? 1;
      const targetCameraRotYOffset = result.cameraRotYOffset ?? 0;
      const targetCameraTiltOffset = result.cameraTiltOffset ?? 0;
      meshAlphaMul += (targetMeshAlphaMul - meshAlphaMul) * OPT_LERP;
      cameraZoom += (targetCameraZoom - cameraZoom) * OPT_LERP;
      meshExtentMul += (targetMeshExtentMul - meshExtentMul) * OPT_LERP;
      cameraRotYOffset += (targetCameraRotYOffset - cameraRotYOffset) * OPT_LERP;
      cameraTiltOffset += (targetCameraTiltOffset - cameraTiltOffset) * OPT_LERP;

      // 2) Lerp entities toward targets. Position/visual lerp fast,
      //    wave params lerp slow (WAVE_LERP), FREQ lerps extra slow
      //    (FREQ_LERP) because freq changes make the wave shape itself
      //    shift — too fast and it "zips" across the mesh.
      for (const e of entities) {
        e.x += (e.tx - e.x) * POS_LERP;
        e.y += (e.ty - e.y) * POS_LERP;
        e.z += (e.tz - e.z) * POS_LERP;
        e.size += (e.targetSize - e.size) * POS_LERP;
        e.alpha += (e.targetAlpha - e.alpha) * POS_LERP;
        e.halo += (e.targetHalo - e.halo) * POS_LERP;
        e.emit += (e.targetEmit - e.emit) * WAVE_LERP;
        e.emitFreq += (e.targetEmitFreq - e.emitFreq) * FREQ_LERP;
        e.emitSpeed += (e.targetEmitSpeed - e.emitSpeed) * WAVE_LERP;
        e.waveR += (e.targetWaveR - e.waveR) * WAVE_LERP;
        e.waveG += (e.targetWaveG - e.waveG) * WAVE_LERP;
        e.waveB += (e.targetWaveB - e.waveB) * WAVE_LERP;
        // Integrated phase — continuous even when emitSpeed changes
        e.phase += e.emitSpeed * dt;
      }

      // 2.5) Stream active wave-emitters to the audio engine so each
      // one gets its own sustained low-mid bass hum whose pitch
      // tracks its visual wave rate. Mapped into 107–210 Hz — the
      // range that ALL consumer speakers (laptop, phone, earbuds)
      // reproduce cleanly. Below 90 Hz cheap drivers buzz/rumble;
      // above 250 Hz the tone turns fatiguing. 107–210 Hz is the
      // gentle "felt drone" sweet spot.
      const audio = audioRef.current;
      if (audio && audio.ready()) {
        const hums: HumSource[] = [];
        for (const e of entities) {
          if (e.emit < 0.08) continue;
          // Exponential mapping keeps emit-freq-changes musical.
          // 90 · 2^(visualHz / 7):
          //   tranquility 1.8 → 107 Hz (A2)
          //   sadness     2.4 → 117 Hz
          //   love        2.6 → 121 Hz
          //   fight-low   3.2 → 132 Hz
          //   friendship  4.4 → 156 Hz (D#3)
          //   happiness   5.5 → 178 Hz
          //   fight-high  7.2 → 212 Hz (G#3)
          //   anger       8.5 → 234 Hz (A#3)
          // All in low-mid bass — clear on every speaker, never sub,
          // never harsh.
          const audioHz = 90 * Math.pow(2, e.emitFreq / 7);
          hums.push({
            id: `e${e.id}`,
            freq: audioHz,
            gain: Math.min(1, Math.max(0, e.emit)),
          });
        }
        audio.setHumSources(hums);
      }

      // 3) Render
      ctx.clearRect(0, 0, w, h);

      const camera: CameraOpts = {
        cx: w / 2,
        cy: h * cameraCenterRatio,
        scale: Math.min(w, h) * 0.28 * cameraZoom,
        rotY: globalT * 0.045 + cameraRotYOffset,
        tiltX: DEFAULT_TILT + cameraTiltOffset,
        perspective: 0.14,
      };

      // Cosmic dust + smoke wisps — atmospheric particles that drift
      // through the scene at all times. Most visible in non-wave
      // scenes (where the mesh doesn't take the show). Gives every
      // beat that "space with dust" depth.
      drawDustField(ctx, camera, globalT);

      // Wave mesh floor — every emitting entity contributes ripples.
      // When no one is emitting, the mesh is flat and reads like the
      // blueprint floor grid. When emissions grow, the plane undulates
      // like an ocean surface with interference patterns between
      // emitters.
      if (meshAlphaMul > 0.01) {
        drawWaveMesh(ctx, camera, entities, meshAlphaMul, meshExtentMul);
      }

      // Decorations that should render UNDER dots (lines, rings)
      for (const deco of result.decorations) {
        renderDeco(ctx, deco, entities, camera);
      }

      // Dots — sorted by depth so near dots render on top.
      // Each entity rides the wave: its rendered y is offset by the
      // local wave height at its (x, z) position, so when the mesh
      // rises under it, the node visibly floats up with it.
      // Flat-mesh wave-ride only applies when the mesh itself is
      // visible. For scenes on other surfaces (globe, etc.) we hide
      // the mesh AND skip this ride so entities don't jiggle from a
      // field that isn't drawn.
      const emittersForRide =
        meshAlphaMul > 0.1 ? entities.filter((e) => e.emit > 0.005) : [];
      const occluder = result.occluderSphere;
      const occluderPj = occluder
        ? project(
            { x: occluder.x, y: occluder.y, z: occluder.z },
            camera
          )
        : null;
      const trails = trailsRef.current!;
      const projected = entities.map((e) => {
        const wave =
          emittersForRide.length > 0
            ? waveHeightAt(e.x, e.z, emittersForRide)
            : 0;
        const rideY = e.y - wave * MESH_AMPLITUDE * 0.5;
        const pj = project({ x: e.x, y: rideY, z: e.z }, camera);
        // Back-hemisphere cull: any entity whose camera-space z is
        // behind the sphere center is on the far side of a solid
        // sphere and should be hidden.
        let occluded = false;
        if (occluderPj && pj.z > occluderPj.z + 0.005) {
          occluded = true;
        }
        return { e, pj, occluded };
      });
      projected.sort((a, b) => a.pj.z - b.pj.z);

      // Push the current projected position into each entity's trail
      // ring buffer (every TRAIL_STRIDE frames to spread the wake
      // further). A stationary entity's trail naturally collapses to
      // its own position — no wake — so this costs nothing visually
      // for parked dots but lights up movers like comets.
      for (const { e, pj, occluded } of projected) {
        const trail = trails[e.id];
        trail.frame++;
        if (trail.frame % TRAIL_STRIDE !== 0) continue;
        if (occluded || e.alpha < 0.02) {
          // Don't push while invisible — prevents a "snap line" from
          // the old off-screen position when an entity fades back in.
          trail.xs[trail.head] = pj.x;
          trail.ys[trail.head] = pj.y;
          trail.alphas[trail.head] = 0;
          trail.radii[trail.head] = 0;
        } else {
          trail.xs[trail.head] = pj.x;
          trail.ys[trail.head] = pj.y;
          trail.alphas[trail.head] = e.alpha;
          trail.radii[trail.head] = 2.2 * e.size;
        }
        trail.head = (trail.head + 1) % TRAIL_SAMPLES;
        if (trail.head === 0) trail.filled = true;
      }

      // ── Pass A · Additive trails (fireball wakes) ───────────────
      // Every moving entity leaves a luminous plume. Drawn with
      // `lighter` so overlapping trails add their light — that's
      // what gives the scene the "space with real physics" feel
      // instead of stickers on black.
      ctx.globalCompositeOperation = "lighter";
      for (const { e, pj, occluded } of projected) {
        if (occluded) continue;
        if (e.alpha < 0.04) continue;
        const trail = trails[e.id];
        const count = trail.filled ? TRAIL_SAMPLES : trail.head;
        if (count < 2) continue;

        const defaultCream =
          Math.abs(e.waveR - 242) < 5 &&
          Math.abs(e.waveG - 238) < 5 &&
          Math.abs(e.waveB - 228) < 5;
        let tr = defaultCream ? 240 : e.waveR;
        let tg = defaultCream ? 232 : e.waveG;
        let tb = defaultCream ? 218 : e.waveB;
        // Lift cream-lit warmth so trails read as hot light, not grey.
        tr = Math.min(255, tr * 0.88 + 30);
        tg = Math.min(255, tg * 0.88 + 22);
        tb = Math.min(255, tb * 0.88 + 14);
        const rgbStr = `${Math.round(tr)}, ${Math.round(tg)}, ${Math.round(tb)}`;

        for (let i = 0; i < count; i++) {
          // Walk backward from head so idx 0 = most recent sample.
          const idx = (trail.head - 1 - i + TRAIL_SAMPLES) % TRAIL_SAMPLES;
          const sA = trail.alphas[idx];
          if (sA < 0.02) continue;
          const age = i / TRAIL_SAMPLES; // 0 newest, 1 oldest
          const fade = 1 - age;
          const tx = trail.xs[idx];
          const ty = trail.ys[idx];
          // Older samples get larger + softer + dimmer — classic
          // velocity-smear comet look.
          const sR = trail.radii[idx] * (2.6 + age * 2.4) * pj.depthT ** 0.5;
          const sAlpha = sA * fade * fade * 0.16;
          if (sAlpha < 0.004) continue;
          const grad = ctx.createRadialGradient(tx, ty, 0, tx, ty, sR);
          grad.addColorStop(0, `rgba(${rgbStr}, ${sAlpha.toFixed(3)})`);
          grad.addColorStop(0.45, `rgba(${rgbStr}, ${(sAlpha * 0.45).toFixed(3)})`);
          grad.addColorStop(1, `rgba(${rgbStr}, 0)`);
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(tx, ty, sR, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalCompositeOperation = "source-over";

      // ── Pass B · Dots ──────────────────────────────────────────
      for (const { e, pj, occluded } of projected) {
        if (occluded) continue;
        if (e.alpha < 0.02) continue;

        // Active heroes use the existing pulse for a breathing feel.
        const pulseAmp = e.pulse
          ? 1 + Math.sin(globalT * 1.2 + e.id * 0.7) * 0.12
          : 1;

        // Always-on subtle twinkle: each entity gets its own frequency
        // and phase derived from its id, so the whole field never
        // breathes in unison. Dimmer entities twinkle proportionally
        // harder, which makes the ambient starfield feel alive
        // without the hero dots flickering.
        const twinkleFreq = 0.35 + ((e.id * 0.13) % 1) * 1.1;
        const twinklePhase = e.id * 1.73;
        const twinkle = Math.sin(globalT * twinkleFreq + twinklePhase);
        const twinkleStrength = 0.1 + (1 - Math.min(1, e.alpha)) * 0.22;

        // Rare sparkle: a sharp peak once in a while per entity.
        // Math.pow(sin, 16) stays near 0 most of the time and briefly
        // spikes to 1 — a blink instead of a wave. Different phase per
        // entity so the whole field never sparkles together.
        const sparklePhase = e.id * 2.37;
        const sparkleBase = Math.sin(globalT * 0.28 + sparklePhase);
        const sparkle = Math.pow(Math.max(0, sparkleBase), 16);

        const alphaMod = 1 + twinkle * twinkleStrength + sparkle * 0.55;
        const haloMod = e.halo + sparkle * 0.18;

        // Use the entity's waveColor as the dot color when it's been
        // set to something non-default. Chapter 1 entities don't set
        // waveColor so they fall back to warmth-based colors.
        const defaultCream =
          Math.abs(e.waveR - 242) < 5 &&
          Math.abs(e.waveG - 238) < 5 &&
          Math.abs(e.waveB - 228) < 5;
        const dotRgb = defaultCream
          ? undefined
          : ([e.waveR, e.waveG, e.waveB] as [number, number, number]);

        drawDot(ctx, pj, {
          radius: 2.2 * e.size * pulseAmp * (1 + sparkle * 0.35),
          warmth: e.warmth,
          alpha: e.alpha * alphaMod,
          halo: haloMod,
          rgb: dotRgb,
        });
      }

      // Labels — always on top
      for (const label of result.labels) {
        renderLabel(ctx, label, entities, camera);
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(raf);
    };
  }, [cameraCenterRatio]);

  // When layout/warmth target changes, propagate to entities' target warmth now.
  useEffect(() => {
    const entities = entitiesRef.current;
    if (!entities) return;
    // Reset target warmth from most recent layout invocation — handled above naturally
    // because layout runs every frame and sets targetWarmth. We just need to lerp colors.
    // Colors don't smooth-interpolate the same way numbers do; we swap with a short
    // cross-fade by sampling alpha blends. For simplicity here we hard-set warmth to target
    // every frame (target warmth is set by the layout). The visual change is driven by
    // alpha+halo which already smooth-interpolate, so the perceived transition is smooth.
    for (const e of entities) {
      e.warmth = e.targetWarmth;
    }
  });

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────
// Wave mesh — deformable floor driven by entity emissions
// ──────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────
// Cosmic dust field — seeded particles that drift slowly through 3D
// space. Two layers: DUST (many small, sharp, bright) for star-like
// specks, and SMOKE (few large, soft, dim) for atmospheric haze that
// reads as nebula gas. Everything is deterministic from seeds so the
// field is stable between renders but animates smoothly over time.
// ──────────────────────────────────────────────────────────────────────

interface DustParticle {
  baseX: number;
  baseY: number;
  baseZ: number;
  driftFreq: number;
  driftAmpX: number;
  driftAmpY: number;
  driftAmpZ: number;
  phase: number;
  size: number;
  alpha: number;
  /** Tiny hue push: +5 = warm cream, -5 = cool cream. */
  hueShift: number;
}

function seededRand(seed: number): number {
  const v = Math.sin(seed * 12.9898) * 43758.5453;
  return v - Math.floor(v);
}

function makeDustLayer(
  count: number,
  opts: {
    xRange: number;
    yRange: number;
    zRange: number;
    sizeMin: number;
    sizeMax: number;
    alphaMin: number;
    alphaMax: number;
    driftFreqMin: number;
    driftFreqMax: number;
    driftAmp: number;
    seedOffset: number;
  }
): DustParticle[] {
  const out: DustParticle[] = [];
  for (let i = 0; i < count; i++) {
    const s = i * 13.7 + opts.seedOffset;
    out.push({
      baseX: (seededRand(s) - 0.5) * opts.xRange,
      baseY: (seededRand(s + 1) - 0.5) * opts.yRange,
      baseZ: (seededRand(s + 2) - 0.5) * opts.zRange,
      driftFreq:
        opts.driftFreqMin +
        seededRand(s + 3) * (opts.driftFreqMax - opts.driftFreqMin),
      driftAmpX: opts.driftAmp * (0.6 + seededRand(s + 4) * 0.8),
      driftAmpY: opts.driftAmp * 0.4 * (0.5 + seededRand(s + 5)),
      driftAmpZ: opts.driftAmp * (0.6 + seededRand(s + 6) * 0.8),
      phase: seededRand(s + 7) * Math.PI * 2,
      size: opts.sizeMin + seededRand(s + 8) * (opts.sizeMax - opts.sizeMin),
      alpha: opts.alphaMin + seededRand(s + 9) * (opts.alphaMax - opts.alphaMin),
      hueShift: (seededRand(s + 10) - 0.5) * 14,
    });
  }
  return out;
}

// Small crisp dust motes — star specks. Many of them, tiny.
// Layered across a wide volume so parallax reads deep and the field
// fills the whole viewport at every camera height.
const DUST_MOTES: ReadonlyArray<DustParticle> = makeDustLayer(420, {
  xRange: 8.5,
  yRange: 2.6,
  zRange: 8.5,
  sizeMin: 0.4,
  sizeMax: 1.6,
  alphaMin: 0.08,
  alphaMax: 0.34,
  driftFreqMin: 0.08,
  driftFreqMax: 0.24,
  driftAmp: 0.09,
  seedOffset: 5,
});

// Large soft smoke blobs — atmospheric haze that reads as nebula gas.
// Bumped count so empty-looking scenes feel like they're set inside a
// volume of lit dust rather than against a void. Kept alpha near the
// floor so they never compete with hero dots; what the eye picks up
// is "full space," not individual blobs.
const DUST_SMOKE: ReadonlyArray<DustParticle> = makeDustLayer(58, {
  xRange: 6.5,
  yRange: 1.8,
  zRange: 6.5,
  sizeMin: 22,
  sizeMax: 62,
  alphaMin: 0.02,
  alphaMax: 0.055,
  driftFreqMin: 0.04,
  driftFreqMax: 0.11,
  driftAmp: 0.16,
  seedOffset: 93,
});

/**
 * Curl-ish flow field — two orthogonal sin stacks that twist the dust
 * particles along coherent streamlines instead of bobbing in place.
 * Not a true divergence-free curl (that needs partial derivatives of
 * a stream function), but close enough visually: particles sweep
 * along smooth arcs and the field looks like it's flowing through a
 * current. Frequencies are intentionally irrational-ish so the field
 * never perfectly repeats.
 */
function curlFlow(
  x: number,
  z: number,
  t: number
): { fx: number; fz: number } {
  const fx =
    Math.sin(x * 0.41 + z * 0.23 + t * 0.11) * 0.5 +
    Math.sin(z * 0.33 - t * 0.07) * 0.3;
  const fz =
    Math.cos(z * 0.37 - x * 0.19 - t * 0.09) * 0.5 +
    Math.cos(x * 0.29 + t * 0.06) * 0.3;
  return { fx, fz };
}

function drawDustField(
  ctx: CanvasRenderingContext2D,
  camera: CameraOpts,
  time: number
): void {
  // Additive compositing — light sums the way it does in real space,
  // so overlapping particles brighten instead of punching through each
  // other. The field reads as lit volumetric gas rather than stacked
  // stickers.
  const prevComp = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = "lighter";

  // Smoke first (behind motes) — large soft radial gradients.
  for (const p of DUST_SMOKE) {
    const tf = time * p.driftFreq;
    const flow = curlFlow(p.baseX, p.baseZ, time);
    const x = p.baseX + Math.sin(tf + p.phase) * p.driftAmpX + flow.fx * p.driftAmpX * 0.55;
    const y = p.baseY + Math.cos(tf * 1.3 + p.phase) * p.driftAmpY;
    const z = p.baseZ + Math.sin(tf * 0.8 + p.phase * 1.3) * p.driftAmpZ + flow.fz * p.driftAmpZ * 0.55;
    const pj = project({ x, y, z }, camera);
    if (pj.depthT < 0.04) continue;
    // Breathing alpha — very slow amplitude wobble so the haze feels
    // like it's drifting through a lit volume.
    const breathe = 0.7 + Math.sin(tf * 0.6 + p.phase) * 0.3;
    const a = p.alpha * pj.depthT * breathe;
    if (a < 0.005) continue;
    const r = p.size * (0.4 + pj.depthT * 0.7);
    const rBase = 236 + p.hueShift;
    const gBase = 230 + p.hueShift * 0.6;
    const bBase = 220 - p.hueShift * 0.3;
    const grad = ctx.createRadialGradient(pj.x, pj.y, 0, pj.x, pj.y, r);
    grad.addColorStop(
      0,
      `rgba(${Math.round(rBase)}, ${Math.round(gBase)}, ${Math.round(bBase)}, ${a.toFixed(3)})`
    );
    grad.addColorStop(0.55, `rgba(${Math.round(rBase)}, ${Math.round(gBase)}, ${Math.round(bBase)}, ${(a * 0.35).toFixed(3)})`);
    grad.addColorStop(1, `rgba(${Math.round(rBase)}, ${Math.round(gBase)}, ${Math.round(bBase)}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(pj.x, pj.y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Dust motes — small, sharp-edged, no gradient. Cheap to draw.
  for (const p of DUST_MOTES) {
    const tf = time * p.driftFreq;
    const flow = curlFlow(p.baseX * 1.3, p.baseZ * 1.3, time);
    const x = p.baseX + Math.sin(tf + p.phase) * p.driftAmpX + flow.fx * p.driftAmpX * 0.7;
    const y = p.baseY + Math.cos(tf * 1.3 + p.phase) * p.driftAmpY;
    const z = p.baseZ + Math.sin(tf * 0.8 + p.phase * 1.3) * p.driftAmpZ + flow.fz * p.driftAmpZ * 0.7;
    const pj = project({ x, y, z }, camera);
    if (pj.depthT < 0.03) continue;
    // Gentle twinkle so motes don't all stay static.
    const twinkle = 0.7 + Math.sin(tf * 3 + p.phase * 2.1) * 0.3;
    const a = p.alpha * (0.4 + pj.depthT * 0.7) * twinkle;
    if (a < 0.01) continue;
    const r = p.size * (0.45 + pj.depthT * 0.6);
    const rCol = 240 + p.hueShift;
    const gCol = 232 + p.hueShift * 0.6;
    const bCol = 218 - p.hueShift * 0.3;
    ctx.fillStyle = `rgba(${Math.round(rCol)}, ${Math.round(gCol)}, ${Math.round(bCol)}, ${a.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(pj.x, pj.y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalCompositeOperation = prevComp;
}

// 48 divisions = 49×49 = 2401 vertices, ~4704 line segments per frame.
// Finer mesh = more samples per wavelength, so the sine surface reads
// as a smooth continuous wave instead of stepped line segments.
// Bumped 48 → 72 so the wave surface reads as continuous even when
// amplitude peaks — at 48 the polygons started showing through at
// the climax of the promise arc. 72 costs ~2.2× grid samples per
// frame (5329 vs 2401) which is trivial on modern hardware.
const MESH_DIVISIONS = 72;
const MESH_EXTENT = 1.9;
const MESH_AMPLITUDE = 0.52;
// Gentler damping so waves fall off smoothly across the field instead
// of cliffing to zero after ~1 unit. Previously 0.95 made crests spike
// near the emitter and die immediately; 0.7 lets the wave spread
// broadly, feel like a real propagating swell.
const MESH_DAMPING = 0.7;

// Secondary and tertiary harmonic scalings per emitter. Real water
// surfaces are multi-octave — a long swell carrying shorter ripples
// riding on top. Mixing 3 octaves at different frequencies/amplitudes
// per emitter turns the sterile single-sine surface into something
// that reads as organic fluid. Frequencies and amps are tuned so the
// sum stays close to unit-amplitude (0.68 + 0.23 + 0.17 ≈ 1.08).
const WAVE_OCT_PRIMARY_AMP = 0.68;
const WAVE_OCT_RIPPLE_AMP = 0.23;
const WAVE_OCT_RIPPLE_FREQ_MUL = 2.15;
const WAVE_OCT_SWELL_AMP = 0.17;
const WAVE_OCT_SWELL_FREQ_MUL = 0.57;

interface WaveSample {
  height: number;
  activity: number;
  r: number;
  g: number;
  b: number;
}

/**
 * Compute the full wave state at a world position: signed height (for
 * peaks/troughs), activity magnitude (for brightness), and the blended
 * color from all nearby emitters (weighted by their local contribution).
 * Waves from multiple people mix their colors the way paint does in
 * water.
 */
function sampleWaveField(
  wx: number,
  wz: number,
  emitters: Entity[]
): WaveSample {
  let height = 0;
  let activity = 0;
  let totalWeight = 0;
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;

  for (const e of emitters) {
    const dx = wx - e.x;
    const dz = wz - e.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    const decay = Math.exp(-dist * MESH_DAMPING);
    const weight = e.emit * decay;
    if (weight < 0.003) continue;

    // Multi-octave superposition — primary swell + faster ripple
    // overlay + slow sub-harmonic underneath. Each uses integrated
    // phase so speed changes stay continuous. Harmonic phase scales
    // with the frequency multiplier so all octaves propagate as one
    // packet (no visual tearing between octaves).
    const phaseArg = dist * e.emitFreq - e.phase;
    const primary = Math.sin(phaseArg) * WAVE_OCT_PRIMARY_AMP;
    const ripple =
      Math.sin(phaseArg * WAVE_OCT_RIPPLE_FREQ_MUL) * WAVE_OCT_RIPPLE_AMP;
    const swell =
      Math.sin(phaseArg * WAVE_OCT_SWELL_FREQ_MUL) * WAVE_OCT_SWELL_AMP;
    const wave = primary + ripple + swell;

    height += weight * wave;
    // Activity weights the primary octave heaviest (it's what drives
    // the felt motion); ripple adds sparkle; swell barely registers.
    activity +=
      weight *
      (Math.abs(primary) + Math.abs(ripple) * 0.55 + Math.abs(swell) * 0.4);
    sumR += weight * e.waveR;
    sumG += weight * e.waveG;
    sumB += weight * e.waveB;
    totalWeight += weight;
  }

  let r = 242;
  let g = 238;
  let b = 228;
  if (totalWeight > 0.003) {
    r = sumR / totalWeight;
    g = sumG / totalWeight;
    b = sumB / totalWeight;
  }
  return { height, activity, r, g, b };
}

function drawWaveMesh(
  ctx: CanvasRenderingContext2D,
  camera: CameraOpts,
  entities: Entity[],
  alphaMul: number,
  extentMul: number
): void {
  const emitters = entities.filter((e) => e.emit > 0.005);

  const effectiveExtent = MESH_EXTENT * extentMul;
  const cols = MESH_DIVISIONS + 1;
  const samples: WaveSample[] = new Array(cols * cols);
  const projected: Array<{ x: number; y: number; z: number; depthT: number }> =
    new Array(cols * cols);
  const step = (2 * effectiveExtent) / MESH_DIVISIONS;
  const baseY = 0.02;

  // Pass 1 — sample wave field at every grid vertex
  for (let j = 0; j <= MESH_DIVISIONS; j++) {
    for (let i = 0; i <= MESH_DIVISIONS; i++) {
      const wx = -effectiveExtent + i * step;
      const wz = -effectiveExtent + j * step;
      const s = sampleWaveField(wx, wz, emitters);
      samples[j * cols + i] = s;
      // Negative y is up on screen, so a positive wave crest = mesh rises
      const y = baseY - s.height * MESH_AMPLITUDE;
      projected[j * cols + i] = project({ x: wx, y, z: wz }, camera);
    }
  }

  // Pass 2 — draw wireframe with per-segment color + brightness.
  // Thinner stroke (0.65) + softer base so the mesh reads as a
  // delicate wavefield, not a lattice. Peak alpha still visible
  // enough to show crests clearly when waves are active.
  ctx.lineWidth = 0.65;
  const baseAlpha = 0.055;
  const waveAlpha = 0.52;

  const drawSegment = (ia: number, ib: number) => {
    const a = projected[ia];
    const b = projected[ib];
    const sa = samples[ia];
    const sb = samples[ib];
    const activity = Math.min(1, (sa.activity + sb.activity) * 0.5);
    const depthBlend = (a.depthT + b.depthT) * 0.5;
    const height = (sa.height + sb.height) * 0.5;
    // Brightness modulation — peaks bright, troughs dim
    const heightGlow = 0.55 + Math.max(0, height) * 0.55;
    const r = (sa.r + sb.r) * 0.5 * heightGlow;
    const g = (sa.g + sb.g) * 0.5 * heightGlow;
    const bl = (sa.b + sb.b) * 0.5 * heightGlow;
    const alpha =
      (baseAlpha + activity * waveAlpha) * (0.45 + depthBlend * 0.7) * alphaMul;
    ctx.strokeStyle = `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(bl)}, ${alpha.toFixed(3)})`;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  };

  for (let j = 0; j <= MESH_DIVISIONS; j++) {
    for (let i = 0; i <= MESH_DIVISIONS; i++) {
      const idx = j * cols + i;
      if (i < MESH_DIVISIONS) drawSegment(idx, idx + 1);
      if (j < MESH_DIVISIONS) drawSegment(idx, idx + cols);
    }
  }
}

/** Sample wave height at a single (wx, wz) so entities can ride the
 *  surface. Cheaper variant — no color needed. Uses integrated phase. */
function waveHeightAt(
  wx: number,
  wz: number,
  emitters: Entity[]
): number {
  let height = 0;
  for (const e of emitters) {
    const dx = wx - e.x;
    const dz = wz - e.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    const decay = Math.exp(-dist * MESH_DAMPING);
    if (e.emit * decay < 0.003) continue;
    // Match the multi-octave wave used in sampleWaveField so entities
    // riding the surface bob with the same fluid height field the mesh
    // is rendering — not a simpler single-sine approximation.
    const phaseArg = dist * e.emitFreq - e.phase;
    const primary = Math.sin(phaseArg) * WAVE_OCT_PRIMARY_AMP;
    const ripple =
      Math.sin(phaseArg * WAVE_OCT_RIPPLE_FREQ_MUL) * WAVE_OCT_RIPPLE_AMP;
    const swell =
      Math.sin(phaseArg * WAVE_OCT_SWELL_FREQ_MUL) * WAVE_OCT_SWELL_AMP;
    height += e.emit * decay * (primary + ripple + swell);
  }
  return height;
}

// ──────────────────────────────────────────────────────────────────────
// Deco + label renderers
// ──────────────────────────────────────────────────────────────────────

function renderDeco(
  ctx: CanvasRenderingContext2D,
  deco: DecoEntry,
  entities: Entity[],
  camera: CameraOpts
): void {
  if (deco.kind === "entityLine") {
    const a = entities[deco.fromId];
    const b = entities[deco.toId];
    if (!a || !b) return;
    const ap = project({ x: a.x, y: a.y, z: a.z }, camera);
    const bp = project({ x: b.x, y: b.y, z: b.z }, camera);
    drawLine(ctx, ap, bp, {
      warmth: deco.warmth,
      alpha: deco.alpha,
      width: deco.width ?? 1,
    });
    return;
  }
  if (deco.kind === "line") {
    const ap = project(deco.from, camera);
    const bp = project(deco.to, camera);
    if (deco.rgb) {
      const rgbStr = `${Math.round(deco.rgb[0])}, ${Math.round(deco.rgb[1])}, ${Math.round(deco.rgb[2])}`;
      const depthBlend = (ap.depthT + bp.depthT) * 0.5;
      const finalAlpha = deco.alpha * (0.35 + depthBlend * 0.8);
      const finalWidth = (deco.width ?? 1) * (0.6 + depthBlend * 0.6);
      ctx.strokeStyle = `rgba(${rgbStr}, ${finalAlpha.toFixed(3)})`;
      ctx.lineWidth = finalWidth;
      ctx.beginPath();
      ctx.moveTo(ap.x, ap.y);
      ctx.lineTo(bp.x, bp.y);
      ctx.stroke();
    } else {
      drawLine(ctx, ap, bp, {
        warmth: deco.warmth,
        alpha: deco.alpha,
        width: deco.width ?? 1,
      });
    }
    return;
  }
  if (deco.kind === "ring") {
    drawRingOnGround(ctx, deco.at, deco.radius, camera, {
      warmth: deco.warmth,
      alpha: deco.alpha,
      width: deco.width ?? 1,
      rgb: deco.rgb,
    });
    return;
  }
  if (deco.kind === "beam") {
    const ap = project(deco.base, camera);
    const bp = project(deco.top, camera);
    const grad = ctx.createLinearGradient(ap.x, ap.y, bp.x, bp.y);
    const rgb = WARMTH_RGB[deco.warmth];
    grad.addColorStop(0, `rgba(${rgb}, ${deco.alpha})`);
    grad.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.strokeStyle = grad;
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.moveTo(ap.x, ap.y);
    ctx.lineTo(bp.x, bp.y);
    ctx.stroke();
    return;
  }
  if (deco.kind === "globe") {
    drawGlobe(ctx, deco, camera, entities);
    return;
  }
  if (deco.kind === "wavePatch") {
    drawWavePatch(ctx, deco, camera);
    return;
  }
  if (deco.kind === "waveLine") {
    const samples = Math.max(12, Math.min(120, deco.samples ?? 40));
    const rgb = deco.rgb ?? WARMTH_RGB[deco.warmth];
    const baseAlpha = deco.alpha;
    ctx.lineWidth = deco.width ?? 1;

    // Sample positions along the line with perpendicular (world y)
    // wave offset. Envelope tapers the wave to 0 at the endpoints so
    // the line visibly starts/ends at its anchor points.
    let prev: { x: number; y: number } | null = null;
    let prevEnv = 0;

    for (let i = 0; i <= samples; i++) {
      const u = i / samples;
      const lx = deco.from.x + (deco.to.x - deco.from.x) * u;
      const ly = deco.from.y + (deco.to.y - deco.from.y) * u;
      const lz = deco.from.z + (deco.to.z - deco.from.z) * u;

      const env = deco.envelope === false ? 1 : Math.sin(u * Math.PI);
      const noiseOffset =
        (deco.noise ?? 0) > 0
          ? Math.sin(u * 47 + deco.phase * 3.7) *
            Math.cos(u * 31 - deco.phase * 1.9) *
            (deco.noise ?? 0)
          : 0;
      const waveY =
        Math.sin(u * deco.frequency * Math.PI * 2 - deco.phase) *
          deco.amplitude *
          env +
        noiseOffset * env;

      const pj = project({ x: lx, y: ly + waveY, z: lz }, camera);

      if (prev && i > 0) {
        const segEnv = (env + prevEnv) * 0.5;
        ctx.strokeStyle = `rgba(${rgb}, ${baseAlpha * segEnv})`;
        ctx.beginPath();
        ctx.moveTo(prev.x, prev.y);
        ctx.lineTo(pj.x, pj.y);
        ctx.stroke();
      }
      prev = { x: pj.x, y: pj.y };
      prevEnv = env;
    }
    return;
  }
  if (deco.kind === "sphereRing") {
    const { center, normal, distance, radius } = deco;
    // Build two orthogonal tangent vectors perpendicular to normal.
    const refX = Math.abs(normal.y) < 0.9 ? 0 : 1;
    const refY = Math.abs(normal.y) < 0.9 ? 1 : 0;
    const dotNRef = refX * normal.x + refY * normal.y;
    let u1x = refX - dotNRef * normal.x;
    let u1y = refY - dotNRef * normal.y;
    let u1z = -dotNRef * normal.z;
    const u1len = Math.sqrt(u1x * u1x + u1y * u1y + u1z * u1z);
    if (u1len < 0.0001) return;
    u1x /= u1len;
    u1y /= u1len;
    u1z /= u1len;
    const u2x = normal.y * u1z - normal.z * u1y;
    const u2y = normal.z * u1x - normal.x * u1z;
    const u2z = normal.x * u1y - normal.y * u1x;
    // Ring center = sphere center + normal * distance.
    const cx = center.x + normal.x * distance;
    const cy = center.y + normal.y * distance;
    const cz = center.z + normal.z * distance;
    const rgbStr = deco.rgb
      ? `${Math.round(deco.rgb[0])}, ${Math.round(deco.rgb[1])}, ${Math.round(deco.rgb[2])}`
      : WARMTH_RGB[deco.warmth];
    ctx.strokeStyle = `rgba(${rgbStr}, ${deco.alpha.toFixed(3)})`;
    ctx.lineWidth = deco.width ?? 1.4;
    const SEGS = 32;
    ctx.beginPath();
    for (let i = 0; i <= SEGS; i++) {
      const a = (i / SEGS) * Math.PI * 2;
      const cA = Math.cos(a);
      const sA = Math.sin(a);
      const wx = cx + (cA * u1x + sA * u2x) * radius;
      const wy = cy + (cA * u1y + sA * u2y) * radius;
      const wz = cz + (cA * u1z + sA * u2z) * radius;
      const pj = project({ x: wx, y: wy, z: wz }, camera);
      if (i === 0) ctx.moveTo(pj.x, pj.y);
      else ctx.lineTo(pj.x, pj.y);
    }
    ctx.stroke();
    return;
  }
  if (deco.kind === "atmosphere") {
    drawAtmosphere(ctx, deco, camera);
    return;
  }
  if (deco.kind === "wavefront") {
    drawWavefront(ctx, deco, camera);
    return;
  }
  if (deco.kind === "dotCloud") {
    const rgb = deco.rgb
      ? `${Math.round(deco.rgb[0])}, ${Math.round(deco.rgb[1])}, ${Math.round(deco.rgb[2])}`
      : WARMTH_RGB[deco.warmth];
    const occPj = deco.occluder
      ? project(
          { x: deco.occluder.x, y: deco.occluder.y, z: deco.occluder.z },
          camera
        )
      : null;

    const perAlphas = deco.alphas;
    for (let i = 0; i < deco.points.length; i++) {
      const p = deco.points[i];
      const pj = project(p, camera);
      // Back-hemisphere dim — points behind the sphere center dim to 15%.
      let alphaMul = 1;
      if (occPj && pj.z > occPj.z) {
        alphaMul = 0.12;
      }
      const perA = perAlphas ? perAlphas[i] ?? 1 : 1;
      // Depth modulation — closer dots slightly brighter.
      const a = deco.alpha * alphaMul * perA * (0.7 + pj.depthT * 0.35);
      if (a < 0.015) continue;
      const r = deco.pixelRadius * (0.75 + pj.depthT * 0.4);
      ctx.fillStyle = `rgba(${rgb}, ${a.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(pj.x, pj.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    return;
  }
}

/**
 * Sample the spherical wave field at a unit-direction point on the
 * sphere surface. Waves travel along great-circle arcs from each
 * emitter, decay with angular distance, and contribute their
 * emitter's color weighted by strength. This mirrors the flat
 * plane's sampleWaveField — same physics, curved domain.
 */
interface SphereWaveSample {
  /** Signed wave height — used to displace mesh vertices radially. */
  height: number;
  /** Unsigned magnitude — used for line brightness/activity. */
  strength: number;
  r: number;
  g: number;
  b: number;
}

function sampleSphereWaveField(
  ux: number, uy: number, uz: number,
  center: { x: number; y: number; z: number },
  emitters: Entity[]
): SphereWaveSample {
  let height = 0;
  let strength = 0;
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  let totalWeight = 0;

  for (const e of emitters) {
    const edx = e.x - center.x;
    const edy = e.y - center.y;
    const edz = e.z - center.z;
    const elen = Math.sqrt(edx * edx + edy * edy + edz * edz);
    if (elen < 0.001) continue;
    const inv = 1 / elen;
    const eux = edx * inv;
    const euy = edy * inv;
    const euz = edz * inv;
    const cosA = Math.max(-1, Math.min(1, ux * eux + uy * euy + uz * euz));
    const angDist = Math.acos(cosA);
    const decay = Math.exp(-angDist * 0.9);
    const wave = Math.sin(angDist * e.emitFreq - e.phase);
    const contrib = e.emit * decay * wave;
    const weight = e.emit * decay * Math.abs(wave);
    if (weight < 0.0015) continue;
    height += contrib;
    strength += weight;
    sumR += weight * e.waveR;
    sumG += weight * e.waveG;
    sumB += weight * e.waveB;
    totalWeight += weight;
  }

  let r = 242, g = 238, b = 228;
  if (totalWeight > 0.002) {
    r = sumR / totalWeight;
    g = sumG / totalWeight;
    b = sumB / totalWeight;
  }
  return { height, strength, r, g, b };
}

/**
 * Render a SOLID globe with the wave field drawn as a displaced
 * wireframe MESH wrapped over it — the same visual language the
 * flat plane uses, just on a sphere.
 *
 * Pass 1 — solid dark sphere body (no holes, ever).
 * Pass 2 — sample a dense lat/lng grid on the sphere. Each vertex
 *   is pushed radially outward by sin-summed wave heights from
 *   every emitter. Connect the grid with lines; color each line
 *   by the blended wave color at its endpoints; amplitude shows
 *   as real mesh displacement above the body. Troughs dip back
 *   toward the surface. Waves travel outward as phase advances.
 *   Front hemisphere only.
 */
function drawGlobe(
  ctx: CanvasRenderingContext2D,
  deco: Extract<DecoEntry, { kind: "globe" }>,
  camera: CameraOpts,
  entities: Entity[]
): void {
  const R = deco.radius;
  const baseRgb = deco.rgb
    ? `${Math.round(deco.rgb[0])}, ${Math.round(deco.rgb[1])}, ${Math.round(deco.rgb[2])}`
    : WARMTH_RGB[deco.warmth];

  // Project the sphere center + two equator points to measure the
  // silhouette's screen radius (orthographic is circular, perspective
  // adds a touch of squash — take the larger axis to be safe).
  const centerPj = project(deco.at, camera);
  const eqA = project({ x: deco.at.x + R, y: deco.at.y, z: deco.at.z }, camera);
  const eqB = project({ x: deco.at.x, y: deco.at.y, z: deco.at.z + R }, camera);
  const pxR = Math.max(
    Math.hypot(eqA.x - centerPj.x, eqA.y - centerPj.y),
    Math.hypot(eqB.x - centerPj.x, eqB.y - centerPj.y)
  );

  // ── Pass 1: solid dark body ──
  const grad = ctx.createRadialGradient(
    centerPj.x - pxR * 0.25,
    centerPj.y - pxR * 0.25,
    0,
    centerPj.x,
    centerPj.y,
    pxR
  );
  grad.addColorStop(0, "rgba(24, 24, 24, 1)");
  grad.addColorStop(0.55, "rgba(14, 14, 14, 1)");
  grad.addColorStop(0.95, "rgba(6, 6, 6, 1)");
  grad.addColorStop(1, "rgba(0, 0, 0, 0.95)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(centerPj.x, centerPj.y, pxR, 0, Math.PI * 2);
  ctx.fill();

  // Faint silhouette edge ring.
  ctx.strokeStyle = `rgba(${baseRgb}, ${(deco.alpha * 0.5).toFixed(3)})`;
  ctx.lineWidth = 0.8;
  ctx.stroke();

  // ── Pass 2: per-emitter local wave patches ──
  // Each emitter draws its OWN small mesh patch on the sphere,
  // centered on its position and bounded in angular radius. Each
  // patch carries that emitter's color and traveling wave. Where
  // two patches overlap on the surface, both render — so you see
  // distinct waves clashing instead of one combined field.
  const emitters = entities.filter((e) => e.emit > 0.008);
  if (emitters.length === 0) return;

  const amp = deco.waveAmplitude;

  for (let emIdx = 0; emIdx < emitters.length; emIdx++) {
    const e = emitters[emIdx];
    const edx = e.x - deco.at.x;
    const edy = e.y - deco.at.y;
    const edz = e.z - deco.at.z;
    const elen = Math.sqrt(edx * edx + edy * edy + edz * edz);
    if (elen < 0.001) continue;
    const inv = 1 / elen;
    const ex = edx * inv;
    const ey = edy * inv;
    const ez = edz * inv;

    // Two orthogonal tangent vectors perpendicular to the emitter.
    const refX = Math.abs(ey) < 0.9 ? 0 : 1;
    const refY = Math.abs(ey) < 0.9 ? 1 : 0;
    const dotEref = refX * ex + refY * ey;
    let u1x = refX - dotEref * ex;
    let u1y = refY - dotEref * ey;
    let u1z = -dotEref * ez;
    const u1len = Math.sqrt(u1x * u1x + u1y * u1y + u1z * u1z);
    u1x /= u1len; u1y /= u1len; u1z /= u1len;
    const u2x = ey * u1z - ez * u1y;
    const u2y = ez * u1x - ex * u1z;
    const u2z = ex * u1y - ey * u1x;

    const freq = Math.max(0.5, e.emitFreq);
    const eRgb = `${Math.round(e.waveR)}, ${Math.round(e.waveG)}, ${Math.round(e.waveB)}`;

    // Patch radius — big enough that adjacent emitters' patches
    // overlap substantially in their bright zones (visible clashing
    // rather than clipping fringes touching).
    const patchAng = 1.35 + ((emIdx * 0.37) % 1) * 0.25; // 1.35–1.6 rad
    // Per-emitter amplitude variation.
    const emAmp = amp * (0.75 + ((emIdx * 0.631) % 1) * 0.5);

    // Slow breathing per emitter.
    const breathPhase = e.phase * 0.12 + emIdx * 0.7;
    const breath = 0.5 + 0.5 * (0.5 + 0.5 * Math.sin(breathPhase));

    // Denser polar mesh — smoother ripple appearance, no visible
    // facets.
    const RADIAL = 14;
    const AZIM = 40;
    interface PV {
      x: number;
      y: number;
      depthT: number;
      height: number;
      envelope: number;
      front: boolean;
    }
    const patch: PV[] = new Array((RADIAL + 1) * (AZIM + 1));

    for (let ri = 0; ri <= RADIAL; ri++) {
      const u = ri / RADIAL;
      const angDist = u * patchAng;
      const cosD = Math.cos(angDist);
      const sinD = Math.sin(angDist);
      // Smooth raised-cosine envelope — full amplitude for inner
      // ~40% of the patch, gentle roll-off to 0 at the outer edge.
      // Produces overlap zones that are still BRIGHT enough to
      // register as colliding waves, not fringes.
      const envR = u < 0.35 ? 1 : 0.5 * (1 + Math.cos(((u - 0.35) / 0.65) * Math.PI));
      // POSITIVE-ONLY wave — peaks lift, troughs flatten to the
      // surface. No geometry dips into the solid body, no jagged
      // spikes. Reads as water crests rolling outward.
      const waveRaw = Math.sin(angDist * freq - e.phase);
      const waveVal = Math.max(0, waveRaw);
      const decay = Math.exp(-angDist * 0.55);
      const localH = waveVal * decay * envR;

      for (let ai = 0; ai <= AZIM; ai++) {
        const theta = (ai / AZIM) * Math.PI * 2;
        const cT = Math.cos(theta);
        const sT = Math.sin(theta);

        const dx = cosD * ex + sinD * (cT * u1x + sT * u2x);
        const dy = cosD * ey + sinD * (cT * u1y + sT * u2y);
        const dz = cosD * ez + sinD * (cT * u1z + sT * u2z);

        const rEff = R + localH * emAmp;
        const wx = deco.at.x + dx * rEff;
        const wy = deco.at.y + dy * rEff;
        const wz = deco.at.z + dz * rEff;
        const pj = project({ x: wx, y: wy, z: wz }, camera);

        patch[ri * (AZIM + 1) + ai] = {
          x: pj.x,
          y: pj.y,
          depthT: pj.depthT,
          height: localH,
          envelope: envR,
          front: pj.z < centerPj.z - 0.002,
        };
      }
    }

    const drawPatchSeg = (a: PV, b: PV): void => {
      if (!a.front && !b.front) return;
      const env = (a.envelope + b.envelope) * 0.5;
      if (env < 0.03) return;
      // Since heights are positive-only, use the raw average as the
      // visible wave magnitude.
      const h = (a.height + b.height) * 0.5;
      const depth = (a.depthT + b.depthT) * 0.5;
      // Gentle brightness — always some visible mesh in lit areas,
      // peaks a little brighter but not saturated.
      const alpha = Math.min(
        0.45,
        (0.04 + h * 0.55) * env * (0.5 + depth * 0.55) * e.emit * breath
      );
      if (alpha < 0.015) return;
      ctx.lineWidth = 0.45 + h * 0.7;
      ctx.strokeStyle = `rgba(${eRgb}, ${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    };

    const pcols = AZIM + 1;
    for (let ri = 0; ri <= RADIAL; ri++) {
      for (let ai = 0; ai <= AZIM; ai++) {
        const idx = ri * pcols + ai;
        if (ai < AZIM) drawPatchSeg(patch[idx], patch[idx + 1]);
        if (ri < RADIAL) drawPatchSeg(patch[idx], patch[idx + pcols]);
      }
    }
  }
}


/**
 * Expanding 3D spherical wavefront — a coherent wave-packet emitted
 * from `origin` whose visual shell is sampled on a Fibonacci lattice
 * on the sphere at the given radius. Each sample pulses with a
 * tangential wave-phase derived from its position + live time, so the
 * shell reads as a propagating surface carrying energy, not a ring of
 * static dots. Intensity is passed in pre-computed (the layout applies
 * the 1/r² falloff); we just modulate alpha + size by it here. Drawn
 * additively so overlapping shells from multiple senders add their
 * light — real constructive interference where wavefronts collide.
 */
function drawWavefront(
  ctx: CanvasRenderingContext2D,
  deco: Extract<DecoEntry, { kind: "wavefront" }>,
  camera: CameraOpts
): void {
  if (deco.intensity < 0.01 || deco.radius < 0.01) return;

  // Shell sample count — kept at 60 for smooth framerate. At most
  // one shell is in flight at a time (MS_MESSAGES spacing), so each
  // shell can feel DENSE without compounding render cost across
  // overlaps. 60 photons still reads as a coherent surface, not a
  // dotted ring.
  const SAMPLES = 60;
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const rgbStr = `${Math.round(deco.rgb[0])}, ${Math.round(deco.rgb[1])}, ${Math.round(deco.rgb[2])}`;

  const prevComp = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = "lighter";

  // Each sample = one photon on the wavefront. Fibonacci lattice
  // gives even surface coverage without poles bunching.
  for (let i = 0; i < SAMPLES; i++) {
    const yFrac = 1 - (i / (SAMPLES - 1)) * 2; // -1..1 (sphere latitude)
    const lat = Math.asin(yFrac);
    const lng = goldenAngle * i;
    const cLat = Math.cos(lat);
    const ux = cLat * Math.cos(lng);
    const uy = -Math.sin(lat); // -y = up in this scene's convention
    const uz = cLat * Math.sin(lng);

    const wx = deco.origin.x + ux * deco.radius;
    const wy = deco.origin.y + uy * deco.radius;
    const wz = deco.origin.z + uz * deco.radius;

    // Cull photons that punched below the mesh (y > -0.05 = below
    // the people's plane); the ground ring handles that part of the
    // visualization so we don't double-draw under the floor.
    if (wy > -0.05) continue;

    const pj = project({ x: wx, y: wy, z: wz }, camera);

    // Wave-packet phase: each photon oscillates in its radial
    // direction at frequency tied to the wavefront's age — so the
    // whole shell pulses coherently as it expands. Doesn't move the
    // photon's projected position (keeps shell shape stable), just
    // modulates its brightness.
    const phase =
      deco.phaseT * 18 +
      i * 0.73 +
      deco.radius * 4.2;
    const packet = 0.55 + 0.45 * Math.sin(phase);

    // Depth modulation — closer photons brighter, with an extra
    // boost at the silhouette where we see edge-on through more
    // atmosphere of the shell (like real limb brightening).
    const depth = 0.55 + pj.depthT * 0.6;

    // Per-photon alpha halved from the previous pass — the shell now
    // reads as coherent but NOT as a flashbang. Overlapping shells
    // still add in `lighter` mode, but cap out visibly below white.
    const alpha = deco.intensity * packet * depth * 0.42;
    if (alpha < 0.01) continue;

    // Photon size — scales with intensity so young bright shells
    // render as thick coherent surfaces, old dim shells thin to
    // specks before vanishing.
    const pR = (1.3 + deco.intensity * 2.2) * depth;

    const grad = ctx.createRadialGradient(pj.x, pj.y, 0, pj.x, pj.y, pR * 2.4);
    grad.addColorStop(0, `rgba(${rgbStr}, ${alpha.toFixed(3)})`);
    grad.addColorStop(0.4, `rgba(${rgbStr}, ${(alpha * 0.45).toFixed(3)})`);
    grad.addColorStop(1, `rgba(${rgbStr}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(pj.x, pj.y, pR * 2.4, 0, Math.PI * 2);
    ctx.fill();

    // Tight core dot for the photon itself — keeps the shell from
    // looking like pure gas by giving each sample a crisp center.
    // Core capped well below opaque so the shell never blows out.
    const coreA = Math.min(0.55, alpha * 1.0);
    ctx.fillStyle = `rgba(${rgbStr}, ${coreA.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(pj.x, pj.y, pR * 0.45, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalCompositeOperation = prevComp;
}

/**
 * Atmospheric halo around a sphere — three layers rendered additively
 * so the result reads as scattered light through a gas shell, not a
 * painted ring:
 *   1. Outer bloom — wide, cool ionosphere scatter (big, very soft)
 *   2. Limb rim — tight warm horizon (hot at the silhouette edge)
 *   3. Tangent wisps — ~28 soft particles drifting on a shell slightly
 *      above the surface, so you see atmosphere physically curling
 *      off the planet edge
 */
function drawAtmosphere(
  ctx: CanvasRenderingContext2D,
  deco: Extract<DecoEntry, { kind: "atmosphere" }>,
  camera: CameraOpts
): void {
  const centerPj = project(deco.at, camera);
  const eqA = project(
    { x: deco.at.x + deco.radius, y: deco.at.y, z: deco.at.z },
    camera
  );
  const eqB = project(
    { x: deco.at.x, y: deco.at.y, z: deco.at.z + deco.radius },
    camera
  );
  const pxR = Math.max(
    Math.hypot(eqA.x - centerPj.x, eqA.y - centerPj.y),
    Math.hypot(eqB.x - centerPj.x, eqB.y - centerPj.y)
  );

  const inner = `${Math.round(deco.innerRgb[0])}, ${Math.round(deco.innerRgb[1])}, ${Math.round(deco.innerRgb[2])}`;
  const outer = `${Math.round(deco.outerRgb[0])}, ${Math.round(deco.outerRgb[1])}, ${Math.round(deco.outerRgb[2])}`;

  const prevComp = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = "lighter";

  // Layer 1 — outer bloom. Big soft cool halo well past the silhouette.
  {
    const r = pxR * 2.3;
    const grad = ctx.createRadialGradient(
      centerPj.x,
      centerPj.y,
      pxR * 0.85,
      centerPj.x,
      centerPj.y,
      r
    );
    const a = deco.alpha * 0.16;
    grad.addColorStop(0, `rgba(${outer}, ${a.toFixed(3)})`);
    grad.addColorStop(0.35, `rgba(${outer}, ${(a * 0.55).toFixed(3)})`);
    grad.addColorStop(0.7, `rgba(${outer}, ${(a * 0.18).toFixed(3)})`);
    grad.addColorStop(1, `rgba(${outer}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(centerPj.x, centerPj.y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Layer 2 — limb rim. Bright, tight, warm — the lit horizon seen
  // from space. Inner-stop starts at 90% of silhouette radius so it
  // hugs the edge without bleeding all over the disc.
  {
    const rOuter = pxR * 1.28;
    const rInner = pxR * 0.92;
    const grad = ctx.createRadialGradient(
      centerPj.x,
      centerPj.y,
      rInner,
      centerPj.x,
      centerPj.y,
      rOuter
    );
    const a = deco.alpha * 0.6;
    grad.addColorStop(0, `rgba(${inner}, 0)`);
    grad.addColorStop(0.35, `rgba(${inner}, ${(a * 0.95).toFixed(3)})`);
    grad.addColorStop(0.62, `rgba(${inner}, ${(a * 0.45).toFixed(3)})`);
    grad.addColorStop(1, `rgba(${inner}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(centerPj.x, centerPj.y, rOuter, 0, Math.PI * 2);
    ctx.fill();
  }

  // Layer 3 — wisp shell. Seeded soft blobs drifting on a thin shell
  // just above the surface (R * 1.04 – 1.12). Deterministic from id
  // so placement is stable but motion is continuous. Only front-
  // hemisphere wisps render (camera-space z < center z).
  const WISP_COUNT = 34;
  for (let i = 0; i < WISP_COUNT; i++) {
    const s = i * 17.31 + 1.7;
    // Unit direction on sphere from seeded lat/lng — Fibonacci lattice
    // adapted by a slow time drift so the shell slowly circulates.
    const yFrac = 1 - (i / (WISP_COUNT - 1)) * 2;
    const lat = Math.asin(yFrac);
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));
    const lng = goldenAngle * i + deco.time * 0.12 + seededRand(s) * Math.PI * 2;
    const cLat = Math.cos(lat);
    const ux = cLat * Math.cos(lng);
    const uy = -Math.sin(lat);
    const uz = cLat * Math.sin(lng);

    // Shell distance breathes slowly.
    const breathe = 1.06 + Math.sin(deco.time * 0.35 + i * 0.9) * 0.045;
    const wx = deco.at.x + ux * deco.radius * breathe;
    const wy = deco.at.y + uy * deco.radius * breathe;
    const wz = deco.at.z + uz * deco.radius * breathe;
    const pj = project({ x: wx, y: wy, z: wz }, camera);

    // Front hemisphere only (closer to camera than center).
    if (pj.z > centerPj.z + 0.005) continue;

    // Wisps near the limb (projected distance from center ≈ pxR) are
    // boosted — that's where real atmosphere glows brightest from space.
    const projDist = Math.hypot(pj.x - centerPj.x, pj.y - centerPj.y);
    const limbT = Math.max(
      0,
      1 - Math.abs(projDist - pxR) / (pxR * 0.35)
    );

    const baseAlpha =
      deco.alpha * (0.06 + limbT * 0.32) * (0.5 + pj.depthT * 0.6);
    if (baseAlpha < 0.008) continue;

    const wispR = pxR * (0.085 + seededRand(s + 4) * 0.12);
    const useInner = limbT > 0.35;
    const rgb = useInner ? inner : outer;
    const grad = ctx.createRadialGradient(pj.x, pj.y, 0, pj.x, pj.y, wispR);
    grad.addColorStop(0, `rgba(${rgb}, ${baseAlpha.toFixed(3)})`);
    grad.addColorStop(
      0.5,
      `rgba(${rgb}, ${(baseAlpha * 0.42).toFixed(3)})`
    );
    grad.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(pj.x, pj.y, wispR, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalCompositeOperation = prevComp;
}

/**
 * Render a localized 3D wave-mesh patch centered at `deco.at`. Samples
 * the emitter's wave function sin(distFromEmitter·freq − phase) across
 * a small disc, lifts each vertex by the wave height, and draws a
 * wireframe. A smooth radial envelope tapers the effect to 0 at the
 * patch edge so the ripple reads as *this emitter's wave* briefly
 * revealed, not a square tile stamped on the ground.
 */
function drawWavePatch(
  ctx: CanvasRenderingContext2D,
  deco: Extract<DecoEntry, { kind: "wavePatch" }>,
  camera: CameraOpts
): void {
  const divisions = 12;
  const cols = divisions + 1;
  const step = (2 * deco.radius) / divisions;
  const baseY = 0.02;

  interface Vtx {
    x: number;
    y: number;
    env: number;
  }
  const vtx: Vtx[] = new Array(cols * cols);

  for (let j = 0; j <= divisions; j++) {
    for (let i = 0; i <= divisions; i++) {
      const wx = deco.at.x - deco.radius + i * step;
      const wz = deco.at.z - deco.radius + j * step;
      // Radial envelope from patch center — cosine taper to 0 at edge.
      const dxC = wx - deco.at.x;
      const dzC = wz - deco.at.z;
      const distC = Math.sqrt(dxC * dxC + dzC * dzC);
      const u = Math.min(1, distC / deco.radius);
      const env = Math.pow(Math.cos((u * Math.PI) / 2), 2);
      // Wave height from the source emitter's traveling wave equation.
      const dxE = wx - deco.emitterX;
      const dzE = wz - deco.emitterZ;
      const distE = Math.sqrt(dxE * dxE + dzE * dzE);
      const wave = Math.sin(distE * deco.frequency - deco.phase);
      const y = baseY - wave * deco.amplitude * env;
      const pj = project({ x: wx, y, z: wz }, camera);
      vtx[j * cols + i] = { x: pj.x, y: pj.y, env };
    }
  }

  const rgb = `${Math.round(deco.rgb[0])}, ${Math.round(deco.rgb[1])}, ${Math.round(deco.rgb[2])}`;
  ctx.lineWidth = 1.0;
  const drawSeg = (ia: number, ib: number): void => {
    const a = vtx[ia];
    const b = vtx[ib];
    const envAvg = (a.env + b.env) * 0.5;
    if (envAvg < 0.02) return;
    ctx.strokeStyle = `rgba(${rgb}, ${(deco.alpha * envAvg).toFixed(3)})`;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  };
  for (let j = 0; j <= divisions; j++) {
    for (let i = 0; i <= divisions; i++) {
      const idx = j * cols + i;
      if (i < divisions) drawSeg(idx, idx + 1);
      if (j < divisions) drawSeg(idx, idx + cols);
    }
  }
}

function drawRingOnGround(
  ctx: CanvasRenderingContext2D,
  at: { x: number; y: number; z: number },
  radius: number,
  camera: CameraOpts,
  opts: {
    warmth: Warmth;
    alpha: number;
    width: number;
    rgb?: readonly [number, number, number];
  }
): void {
  const segs = 48;
  const rgb = opts.rgb
    ? `${Math.round(opts.rgb[0])}, ${Math.round(opts.rgb[1])}, ${Math.round(opts.rgb[2])}`
    : WARMTH_RGB[opts.warmth];
  ctx.strokeStyle = `rgba(${rgb}, ${opts.alpha})`;
  ctx.lineWidth = opts.width;
  ctx.beginPath();
  for (let s = 0; s <= segs; s++) {
    const a = (s / segs) * Math.PI * 2;
    const p = project(
      { x: at.x + Math.cos(a) * radius, y: at.y, z: at.z + Math.sin(a) * radius },
      camera
    );
    if (s === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();
}

function renderLabel(
  ctx: CanvasRenderingContext2D,
  label: LabelEntry,
  entities: Entity[],
  camera: CameraOpts
): void {
  const anchor =
    label.entityId !== undefined
      ? entities[label.entityId]
      : label.at
      ? { x: label.at.x, y: label.at.y, z: label.at.z }
      : null;
  if (!anchor) return;
  const pj = project(
    { x: anchor.x, y: anchor.y, z: anchor.z },
    camera
  );
  drawLabel(ctx, pj, label.text, {
    offsetY: label.offsetY ?? -14,
    warmth: label.warmth ?? "cream",
    alpha: label.alpha ?? 0.65,
    size: label.size ?? 10,
    align: "center",
  });
}
